import {
  CLIENT_DOCUMENT_TYPES,
  formatClientDocument,
  normalizeClientDocument,
} from "./validationHelper";

export const GLOBAL_SEARCH_TYPES = Object.freeze({
  ALL: "ALL",
  NAME: "NAME",
  CPF: "CPF",
  CNPJ: "CNPJ",
  POSTCODE: "POSTCODE",
  EMAIL: "EMAIL",
  MATTER: "MATTER",
  MASTER_SCHEDULE: "MASTER_SCHEDULE",
});

export const GLOBAL_SEARCH_TYPE_OPTIONS = Object.values(GLOBAL_SEARCH_TYPES);

export const GLOBAL_SEARCH_PATH = "/coordinator/search";

export const canUseCoordinatorGlobalSearch = (features, user) =>
  features?.coordinatorGlobalSearch === true
  && user?.capabilities?.canAccessCoordinator === true;

// Advanced search has its own flag (SSM `advanced_client_search`) and serves both roles.
export const canUseAdvancedClientSearch = (features) => features?.advancedClientSearch === true;

export const getLegacySearchPath = (features) =>
  features?.clientSearch ? "/client-search" : "/cpf-search";

// Backend error codes ({ error, message }); the UI shows its own pt/en text per code,
// never the English `message`. GENERIC covers network failures and unknown codes.
export const GLOBAL_SEARCH_ERRORS = Object.freeze({
  INVALID_SEARCH: "INVALID_SEARCH",
  UNAUTHORIZED: "UNAUTHORIZED",
  FEATURE_DISABLED: "FEATURE_DISABLED",
  FORBIDDEN: "FORBIDDEN",
  UPSTREAM_ERROR: "UPSTREAM_ERROR",
  INTERNAL_SERVER_ERROR: "INTERNAL_SERVER_ERROR",
  GENERIC: "GENERIC",
});

const ERROR_BY_STATUS = {
  400: GLOBAL_SEARCH_ERRORS.INVALID_SEARCH,
  401: GLOBAL_SEARCH_ERRORS.UNAUTHORIZED,
  403: GLOBAL_SEARCH_ERRORS.FORBIDDEN,
  500: GLOBAL_SEARCH_ERRORS.INTERNAL_SERVER_ERROR,
  502: GLOBAL_SEARCH_ERRORS.UPSTREAM_ERROR,
};

// GLOBAL result limit for the results page (backend accepts 1..200, default 100).
export const GLOBAL_SEARCH_PAGE_LIMIT = 100;

// Mirrors the backend minimums: 3+ letters for name, 4+ digits for numbers and
// matter numbers, 3+ characters for e-mail and master schedule; 100 characters max.
export const MIN_NAME_LETTERS = 3;
export const MIN_NUMBER_DIGITS = 4;
export const MIN_EMAIL_CHARS = 3;
export const MIN_MASTER_SCHEDULE_CHARS = 3;
export const MAX_QUERY_LENGTH = 100;
export const MATTER_NUMBER_PATTERN = /^mat-?\d/i;
export const MASTER_SCHEDULE_PATTERN = /^(ms[123]-|ori_)/i;

export const normalizeGlobalSearchType = (value) =>
  GLOBAL_SEARCH_TYPE_OPTIONS.includes(value) ? value : GLOBAL_SEARCH_TYPES.ALL;

const countLetters = (value) => (String(value).match(/\p{L}/gu) || []).length;
const countDigits = (value) => (String(value).match(/\d/g) || []).length;

// Returns null when valid, otherwise the translation key suffix of the problem.
export const validateGlobalSearch = (query, searchType) => {
  const trimmed = String(query ?? "").trim();
  if (!trimmed) return "required";
  if (trimmed.length > MAX_QUERY_LENGTH) return "tooLong";

  if (searchType === GLOBAL_SEARCH_TYPES.NAME) {
    return countLetters(trimmed) >= MIN_NAME_LETTERS ? null : "name";
  }

  if (searchType === GLOBAL_SEARCH_TYPES.EMAIL) {
    return trimmed.length >= MIN_EMAIL_CHARS ? null : "email";
  }

  if (searchType === GLOBAL_SEARCH_TYPES.MATTER) {
    return countDigits(trimmed) >= MIN_NUMBER_DIGITS ? null : "matter";
  }

  if (searchType === GLOBAL_SEARCH_TYPES.MASTER_SCHEDULE) {
    return trimmed.length >= MIN_MASTER_SCHEDULE_CHARS ? null : "masterSchedule";
  }

  if (searchType === GLOBAL_SEARCH_TYPES.ALL) {
    if (trimmed.includes("@")) {
      return trimmed.length >= MIN_EMAIL_CHARS ? null : "all";
    }
    if (MATTER_NUMBER_PATTERN.test(trimmed)) {
      return countDigits(trimmed) >= MIN_NUMBER_DIGITS ? null : "matter";
    }
    if (MASTER_SCHEDULE_PATTERN.test(trimmed)) return null;
    if (countLetters(trimmed) > 0) {
      return countLetters(trimmed) >= MIN_NAME_LETTERS ? null : "all";
    }
    return countDigits(trimmed) >= MIN_NUMBER_DIGITS ? null : "all";
  }

  return countDigits(trimmed) >= MIN_NUMBER_DIGITS ? null : "number";
};

// Same auto-detection the backend applies to ALL searches.
export const detectGlobalSearchKinds = (query) => {
  const trimmed = String(query ?? "").trim();
  if (!trimmed) return [];
  if (trimmed.includes("@")) return [GLOBAL_SEARCH_TYPES.EMAIL];
  if (MATTER_NUMBER_PATTERN.test(trimmed)) return [GLOBAL_SEARCH_TYPES.MATTER];
  if (MASTER_SCHEDULE_PATTERN.test(trimmed)) return [GLOBAL_SEARCH_TYPES.MASTER_SCHEDULE];
  if (countLetters(trimmed) > 0) return [GLOBAL_SEARCH_TYPES.NAME];
  const kinds = [GLOBAL_SEARCH_TYPES.CPF, GLOBAL_SEARCH_TYPES.CNPJ, GLOBAL_SEARCH_TYPES.POSTCODE];
  if (countDigits(trimmed) >= 6) kinds.push(GLOBAL_SEARCH_TYPES.MATTER);
  return kinds;
};

export const buildGlobalSearchPayload = (query, searchType, limit) => ({
  searchMode: "GLOBAL",
  query: String(query ?? "").trim(),
  searchType: normalizeGlobalSearchType(searchType),
  ...(limit ? { limit } : {}),
});

export const buildGlobalSearchUrl = (query, searchType = GLOBAL_SEARCH_TYPES.ALL) => {
  const params = new URLSearchParams({
    q: String(query ?? "").trim(),
    type: normalizeGlobalSearchType(searchType),
  });
  return `${GLOBAL_SEARCH_PATH}?${params.toString()}`;
};

export const mapGlobalSearchError = (error) => {
  const code = String(error?.code || "").toUpperCase();
  if (code && code !== GLOBAL_SEARCH_ERRORS.GENERIC && GLOBAL_SEARCH_ERRORS[code]) return code;
  return ERROR_BY_STATUS[error?.status] || GLOBAL_SEARCH_ERRORS.GENERIC;
};

// Only well-formed values are reformatted; masked values (with "*") render as-is.
export const formatSearchDocument = (document, documentType) => {
  if (document === null || document === undefined || document === "") return null;
  if (!/^[\d.\-/\s]+$/.test(String(document))) return String(document);
  const digits = normalizeClientDocument(document);
  if (!digits) return String(document);
  if (documentType === CLIENT_DOCUMENT_TYPES.CNPJ && digits.length === 14) {
    return formatClientDocument(digits, CLIENT_DOCUMENT_TYPES.CNPJ);
  }
  if (documentType === CLIENT_DOCUMENT_TYPES.CPF && digits.length === 11) {
    return formatClientDocument(digits, CLIENT_DOCUMENT_TYPES.CPF);
  }
  return String(document);
};

export const formatPostalCode = (postalCode) => {
  if (!postalCode) return null;
  const value = String(postalCode).trim();
  if (/^\d{5}-?\d{3}$/.test(value)) {
    const digits = normalizeClientDocument(value);
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  }
  return value;
};

export const formatSearchAddress = (address) => {
  if (!address) return null;
  const cityState = [address.city, address.state].filter(Boolean).join(" - ");
  const postalCode = formatPostalCode(address.postalCode);
  const parts = [
    address.street,
    cityState,
    postalCode ? `CEP ${postalCode}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
};

const MATTER_STATUS_KEYS = {
  open: "open",
  reopened: "reopened",
  pendingtermination: "pendingTermination",
  terminated: "terminated",
};

export const getMatterStatusKey = (status) =>
  MATTER_STATUS_KEYS[String(status || "").replace(/[\s_-]/g, "").toLowerCase()] || null;

// Backend case types ("BHP-Individuals", "BHP-Indigenous", "BHP-Business", "Mariana 2026",
// and "ARD" for every Unicorn type) are shown with translated labels; API values are
// never changed. Older "Unicorn…"/"…-Businesses" spellings are still recognised.
const CASE_TYPE_PROGRAMS = [
  ["MARIANA_2026", /mariana\s*2026/],
  ["BHP", /\bbhp\b/],
  ["ARD", /\b(ard|unicorn)\b/],
];
const CASE_TYPE_SEGMENTS = [
  ["BUSINESSES", /business/],
  ["INDIGENOUS", /indigenous|ind[ií]gena/],
  ["INDIVIDUALS", /individual/],
];

export const getCaseTypeLabel = (caseType, t) => {
  const raw = String(caseType || "").trim();
  if (!raw) return "";
  const normalized = raw.toLowerCase();
  const program = CASE_TYPE_PROGRAMS.find(([, pattern]) => pattern.test(normalized))?.[0];
  if (!program) return raw;
  const programLabel = t(`globalSearch.caseTabs.${program}`);
  const segment = CASE_TYPE_SEGMENTS.find(([, pattern]) => pattern.test(normalized))?.[0];
  return segment ? `${programLabel} – ${t(`globalSearch.caseSegments.${segment}`)}` : programLabel;
};

export const getCaseTabBadgeClass = (caseTab) => {
  const rawKey = String(caseTab || "").toLowerCase();
  // Mariana 2026 belongs to the English claim, so it shares the BHP badge.
  const key = rawKey === "mariana_2026" ? "bhp" : rawKey;
  return ["bhp", "ard"].includes(key)
    ? `case-badge-${key}`
    : "case-badge-other";
};

export const buildClientRecordPath = (result) =>
  result?.cocoId && result?.matterId
    ? `/coordinator/${encodeURIComponent(result.cocoId)}/clients/${encodeURIComponent(result.matterId)}`
    : null;

// --- Typeahead ---------------------------------------------------------------

export const TYPEAHEAD_DEBOUNCE_MS = 300;
export const TYPEAHEAD_MAX_RESULTS = 8;
export const TYPEAHEAD_GROUPS = Object.freeze({ CLIENTS: "clients", MATTERS: "matters" });

const MATCH_KINDS = [
  GLOBAL_SEARCH_TYPES.NAME,
  GLOBAL_SEARCH_TYPES.CPF,
  GLOBAL_SEARCH_TYPES.CNPJ,
  GLOBAL_SEARCH_TYPES.POSTCODE,
  GLOBAL_SEARCH_TYPES.EMAIL,
  GLOBAL_SEARCH_TYPES.MATTER,
  GLOBAL_SEARCH_TYPES.MASTER_SCHEDULE,
  "CITY",
];

// Prefers the backend's matchedOn; older responses fall back to query detection.
export const getResultMatchedOn = (result, query) => {
  const fromBackend = Array.isArray(result?.matchedOn)
    ? result.matchedOn.map((kind) => String(kind).toUpperCase()).filter((kind) => MATCH_KINDS.includes(kind))
    : [];
  if (fromBackend.length) return fromBackend;
  const detected = detectGlobalSearchKinds(query);
  if (detected.includes(GLOBAL_SEARCH_TYPES.CPF)) {
    return [result?.documentType === "CNPJ" ? GLOBAL_SEARCH_TYPES.CNPJ : GLOBAL_SEARCH_TYPES.CPF];
  }
  return detected.slice(0, 1);
};

export const getTypeaheadGroup = (matchedOn) =>
  matchedOn.includes(GLOBAL_SEARCH_TYPES.MATTER) ? TYPEAHEAD_GROUPS.MATTERS : TYPEAHEAD_GROUPS.CLIENTS;

export const getResultMatterNumber = (result) =>
  result?.matterNumber || (MATTER_NUMBER_PATTERN.test(result?.matterName || "") ? result.matterName : null);

// The value shown on the second line: what actually matched, formatted.
export const getMatchedValue = (result, matchedOn) => {
  const document = formatSearchDocument(result?.document, result?.documentType);
  const postalCode = formatPostalCode(result?.address?.postalCode);
  const byKind = {
    [GLOBAL_SEARCH_TYPES.MATTER]: getResultMatterNumber(result),
    [GLOBAL_SEARCH_TYPES.EMAIL]: result?.email || null,
    [GLOBAL_SEARCH_TYPES.CPF]: document,
    [GLOBAL_SEARCH_TYPES.CNPJ]: document,
    [GLOBAL_SEARCH_TYPES.POSTCODE]: postalCode ? `CEP ${postalCode}` : null,
    [GLOBAL_SEARCH_TYPES.MASTER_SCHEDULE]: result?.masterSchedule || null,
  };
  const kind = [
    GLOBAL_SEARCH_TYPES.MATTER,
    GLOBAL_SEARCH_TYPES.MASTER_SCHEDULE,
    GLOBAL_SEARCH_TYPES.EMAIL,
    GLOBAL_SEARCH_TYPES.CPF,
    GLOBAL_SEARCH_TYPES.CNPJ,
    GLOBAL_SEARCH_TYPES.POSTCODE,
  ].find((candidate) => matchedOn.includes(candidate) && byKind[candidate]);
  if (kind) return { kind, value: byKind[kind] };
  if (document) return { kind: result?.documentType === "CNPJ" ? "CNPJ" : "CPF", value: document };
  const matterNumber = getResultMatterNumber(result);
  return matterNumber ? { kind: GLOBAL_SEARCH_TYPES.MATTER, value: matterNumber } : { kind: null, value: null };
};

export const buildTypeaheadGroups = (results, query, max = TYPEAHEAD_MAX_RESULTS) => {
  const items = (Array.isArray(results) ? results : []).slice(0, max).map((result, index) => {
    const matchedOn = getResultMatchedOn(result, query);
    return {
      id: `${result?.matterId || "matter"}-${result?.partyId || "party"}-${index}`,
      result,
      matchedOn,
      group: getTypeaheadGroup(matchedOn),
      matched: getMatchedValue(result, matchedOn),
    };
  });
  return [TYPEAHEAD_GROUPS.CLIENTS, TYPEAHEAD_GROUPS.MATTERS]
    .map((group) => ({ group, items: items.filter((item) => item.group === group) }))
    .filter(({ items: groupItems }) => groupItems.length > 0);
};

const foldChar = (char) => char.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase();

// Splits text into [{ text, match }] segments. Letters compare accent/case
// insensitively; digit-only needles ignore punctuation (123.456 vs 123456).
export const getHighlightSegments = (text, query) => {
  const source = String(text ?? "");
  const needleRaw = String(query ?? "").trim();
  if (!source || !needleRaw) return source ? [{ text: source, match: false }] : [];

  const marks = new Array(source.length).fill(false);
  const digitsOnly = !/\p{L}|@/u.test(needleRaw);

  if (digitsOnly) {
    const needle = needleRaw.replace(/\D/g, "");
    const positions = [];
    let digits = "";
    Array.from(source).forEach((char, index) => {
      if (/\d/.test(char)) {
        digits += char;
        positions.push(index);
      }
    });
    if (needle.length) {
      let from = digits.indexOf(needle);
      while (from !== -1) {
        // Include separators inside the match (123.456 highlights as one run).
        for (let i = positions[from]; i <= positions[from + needle.length - 1]; i += 1) {
          marks[i] = true;
        }
        from = digits.indexOf(needle, from + needle.length);
      }
    }
  } else {
    const folded = Array.from(source).map(foldChar);
    const haystack = folded.join("");
    if (haystack.length === source.length) {
      const needles = needleRaw.includes("@") || MATTER_NUMBER_PATTERN.test(needleRaw)
        ? [needleRaw]
        : needleRaw.split(/\s+/).filter((token) => token.length >= 2);
      needles.map((token) => Array.from(token).map(foldChar).join("")).forEach((needle) => {
        let from = haystack.indexOf(needle);
        while (needle && from !== -1) {
          for (let i = from; i < from + needle.length; i += 1) marks[i] = true;
          from = haystack.indexOf(needle, from + needle.length);
        }
      });
    }
  }

  const segments = [];
  Array.from(source).forEach((char, index) => {
    const last = segments[segments.length - 1];
    if (last && last.match === marks[index]) last.text += char;
    else segments.push({ text: char, match: marks[index] });
  });
  return segments;
};
